import { c as _c } from "react/compiler-runtime";
import { IconArchive, IconBell, IconBellCog, IconChecks, IconInbox } from "@tabler/icons-react";
import Link from "next/link";
import { useEffect, useRef, useState, useTransition } from "react";
import { NotificationList } from "@/components/notifications/notification-list";
import { useNotificationsData } from "@/hooks/use-notifications-data";
import type { NotificationData } from "@domainstack/types";
import { Button } from "@domainstack/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@domainstack/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@domainstack/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
export function NotificationsPopover() {
  const $ = _c(91);
  const loadMoreRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const [view, setView] = useState("inbox");
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  const autoMarkedThisOpenRef = useRef(false);
  const skipAutoMarkOnCloseRef = useRef(false);
  const filter = view === "inbox" ? "unread" : "read";
  let t0;
  if ($[0] !== filter || $[1] !== open) {
    t0 = {
      filter,
      enabled: open
    };
    $[0] = filter;
    $[1] = open;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    notifications,
    count,
    showLoading,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isError: isNotificationsError,
    markAllRead,
    markRead,
    fetchNextPage,
    getLatestUnreadCount
  } = useNotificationsData(t0);
  let t1;
  let t2;
  if ($[3] !== open) {
    t1 = () => {
      if (open) {
        autoMarkedThisOpenRef.current = false;
        skipAutoMarkOnCloseRef.current = false;
      }
    };
    t2 = [open];
    $[3] = open;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== getLatestUnreadCount || $[7] !== isNotificationsError || $[8] !== markAllRead || $[9] !== notifications || $[10] !== showLoading || $[11] !== view) {
    t3 = () => {
      if (autoMarkedThisOpenRef.current) {
        return;
      }
      if (markAllRead.isPending) {
        return;
      }
      if (view !== "inbox") {
        return;
      }
      if (showLoading || isNotificationsError) {
        return;
      }
      if (notifications.length === 0) {
        return;
      }
      const latestUnreadCount = getLatestUnreadCount();
      if (latestUnreadCount <= 0) {
        return;
      }
      const upTo = new Date(Math.max(...notifications.map(_temp)));
      autoMarkedThisOpenRef.current = true;
      markAllRead.mutate({
        upTo
      }, {
        onError: () => {
          autoMarkedThisOpenRef.current = false;
        }
      });
    };
    $[6] = getLatestUnreadCount;
    $[7] = isNotificationsError;
    $[8] = markAllRead;
    $[9] = notifications;
    $[10] = showLoading;
    $[11] = view;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const maybeAutoMarkAllRead = t3;
  let t4;
  if ($[13] !== maybeAutoMarkAllRead) {
    t4 = () => {
      maybeAutoMarkAllRead();
      setOpen(false);
    };
    $[13] = maybeAutoMarkAllRead;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const closePopover = t4;
  let t5;
  if ($[15] !== markRead) {
    t5 = notification => {
      if (!notification.readAt) {
        markRead.mutate({
          id: notification.id
        });
      }
      skipAutoMarkOnCloseRef.current = true;
      setOpen(false);
    };
    $[15] = markRead;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleNotificationClick = t5;
  let t6;
  let t7;
  if ($[17] !== fetchNextPage || $[18] !== hasNextPage || $[19] !== isFetchNextPageError || $[20] !== isFetchingNextPage || $[21] !== open) {
    t6 = () => {
      const scrollContainer = scrollAreaRef.current;
      const loadMoreElement = loadMoreRef.current;
      if (!open || !loadMoreElement || !hasNextPage || isFetchingNextPage || isFetchNextPageError) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1,
        root: scrollContainer
      });
      observer.observe(loadMoreElement);
      return () => observer.disconnect();
    };
    t7 = [fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError, open];
    $[17] = fetchNextPage;
    $[18] = hasNextPage;
    $[19] = isFetchNextPageError;
    $[20] = isFetchingNextPage;
    $[21] = open;
    $[22] = t6;
    $[23] = t7;
  } else {
    t6 = $[22];
    t7 = $[23];
  }
  useEffect(t6, t7);
  let t8;
  if ($[24] !== maybeAutoMarkAllRead) {
    t8 = nextOpen => {
      if (!nextOpen) {
        if (skipAutoMarkOnCloseRef.current) {
          skipAutoMarkOnCloseRef.current = false;
        } else {
          maybeAutoMarkAllRead();
        }
      }
      setOpen(nextOpen);
    };
    $[24] = maybeAutoMarkAllRead;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = `Notifications${count > 0 ? ` (${count})` : ""}`;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconBell />;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== count) {
    t11 = count > 0 && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-destructive ring-2 ring-background" aria-hidden={true} />;
    $[27] = count;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t9) {
    t12 = <TooltipTrigger render={<PopoverTrigger render={<Button variant="ghost" size="icon-sm" className="relative" aria-label={t9}>{t10}{t11}</Button>} />} />;
    $[29] = t11;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = count > 0 ? `Notifications (${count})` : "Notifications";
  let t14;
  if ($[32] !== t13) {
    t14 = <TooltipContent>{t13}</TooltipContent>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <Tooltip>{t12}{t14}</Tooltip>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      side: "none",
      align: "shift"
    };
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h4 className="text-[15px] font-semibold">Notifications</h4>;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <IconBellCog className="size-3.5 shrink-0 text-foreground/90" />;
    t19 = <span className="sr-only">Settings</span>;
    $[39] = t18;
    $[40] = t19;
  } else {
    t18 = $[39];
    t19 = $[40];
  }
  let t20;
  if ($[41] !== closePopover) {
    t20 = <TooltipTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Notification settings" nativeButton={false} render={<Link href="/settings/notifications" onNavigate={closePopover}>{t18}{t19}</Link>} />} />;
    $[41] = closePopover;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <TooltipContent>Settings</TooltipContent>;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t20) {
    t22 = <div className="flex items-center justify-between pt-2 pr-4 pb-1 pl-4">{t17}<Tooltip>{t20}{t21}</Tooltip></div>;
    $[44] = t20;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== maybeAutoMarkAllRead || $[47] !== startTransition || $[48] !== view) {
    t23 = v => {
      const nextView = v as typeof view;
      if (view === "inbox" && nextView === "archive") {
        maybeAutoMarkAllRead();
      }
      startTransition(() => setView(nextView));
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = 0;
      }
    };
    $[46] = maybeAutoMarkAllRead;
    $[47] = startTransition;
    $[48] = view;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconInbox className="!text-inherit" aria-hidden={true} />;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== count) {
    t25 = count > 0 && <span className="ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-medium text-background tabular-nums">{count > 99 ? "99+" : count}</span>;
    $[51] = count;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t25) {
    t26 = <TabsTrigger value="inbox" className="flex-initial gap-2 text-[13px] transition-colors hover:text-foreground">{t24}Inbox{t25}</TabsTrigger>;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <TabsTrigger value="archive" className="flex-initial gap-2 text-[13px] transition-colors hover:text-foreground"><IconArchive className="!text-inherit" aria-hidden={true} />Archive</TabsTrigger>;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== count || $[57] !== markAllRead || $[58] !== view) {
    t28 = view === "inbox" && count > 0 && <div className="ml-auto flex items-center"><Button variant="ghost" size="sm" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending} className="h-full text-[13px] text-muted-foreground" aria-label="Clear all notifications"><IconChecks className="size-4" aria-hidden={true} /><span className="max-sm:sr-only">Clear all</span></Button></div>;
    $[56] = count;
    $[57] = markAllRead;
    $[58] = view;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t26 || $[61] !== t28) {
    t29 = <TabsList variant="line">{t26}{t27}{t28}</TabsList>;
    $[60] = t26;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t23 || $[64] !== t29 || $[65] !== view) {
    t30 = <div className="pl-1.5"><Tabs value={view} onValueChange={t23}>{t29}</Tabs></div>;
    $[63] = t23;
    $[64] = t29;
    $[65] = view;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t22 || $[68] !== t30) {
    t31 = <div className="shrink-0 border-b border-border bg-card/60">{t22}{t30}</div>;
    $[67] = t22;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  const t32 = isNotificationsError && notifications.length === 0;
  let t33;
  if ($[70] !== fetchNextPage) {
    t33 = () => void fetchNextPage();
    $[70] = fetchNextPage;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== closePopover || $[73] !== handleNotificationClick || $[74] !== hasNextPage || $[75] !== isFetchNextPageError || $[76] !== isFetchingNextPage || $[77] !== notifications || $[78] !== showLoading || $[79] !== t32 || $[80] !== t33 || $[81] !== view) {
    t34 = <NotificationList notifications={notifications} isLoading={showLoading} isError={t32} view={view} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} isFetchNextPageError={isFetchNextPageError} onRetryNextPage={t33} loadMoreRef={loadMoreRef} scrollAreaRef={scrollAreaRef} onNotificationClick={handleNotificationClick} onClosePopover={closePopover} />;
    $[72] = closePopover;
    $[73] = handleNotificationClick;
    $[74] = hasNextPage;
    $[75] = isFetchNextPageError;
    $[76] = isFetchingNextPage;
    $[77] = notifications;
    $[78] = showLoading;
    $[79] = t32;
    $[80] = t33;
    $[81] = view;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== t31 || $[84] !== t34) {
    t35 = <PopoverContent className="overflow-hidden bg-background p-0 max-sm:!right-0 max-sm:!left-0 max-sm:!mx-auto max-sm:w-[calc(100vw-1rem)] max-sm:!translate-x-0 sm:w-[400px]" align="end" side="bottom" collisionAvoidance={t16} collisionPadding={8}><div className="flex max-h-[calc(min(100dvh-6rem,560px))] flex-col">{t31}{t34}</div></PopoverContent>;
    $[83] = t31;
    $[84] = t34;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== open || $[87] !== t15 || $[88] !== t35 || $[89] !== t8) {
    t36 = <Popover open={open} onOpenChange={t8}>{t15}{t35}</Popover>;
    $[86] = open;
    $[87] = t15;
    $[88] = t35;
    $[89] = t8;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  return t36;
}
function _temp(n) {
  return n.sentAt.getTime();
}